import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import type { PartialBlock } from "@blocknote/core";

import type { Doc } from "../../shared/types";
import { AUDIENCE_LABEL, KIND_LABEL, STATUS_LABEL } from "../../shared/types";
import { api } from "../api";
import { formatDate } from "../format";
import { ArrowLeftIcon } from "../components/icons";

export function parseBlocks(contentJson: string): PartialBlock[] | undefined {
  try {
    const parsed = JSON.parse(contentJson);
    return Array.isArray(parsed) && parsed.length > 0 ? (parsed as PartialBlock[]) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read view. Rendering through BlockNote rather than a separate markdown
 * renderer guarantees the page looks exactly like the editor did.
 */
function Reader({ contentJson }: { contentJson: string }) {
  const blocks = useMemo(() => parseBlocks(contentJson), [contentJson]);
  const editor = useCreateBlockNote({ initialContent: blocks }, [contentJson]);
  return <BlockNoteView editor={editor} editable={false} theme="dark" />;
}

export default function DocPage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState<Doc | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setDoc(null);
    setError(null);
    api
      .get(slug)
      .then((result) => live && setDoc(result.document))
      .catch((err: Error) => live && setError(err.message));
    return () => {
      live = false;
    };
  }, [slug]);

  if (error) {
    return (
      <div className="shell">
        <Link className="backlink" to="/">
          <ArrowLeftIcon /> Handbook
        </Link>
        <div className="notice">
          {error === "not_found" ? "That document does not exist." : error}
        </div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="shell">
        <div className="notice">Loading…</div>
      </div>
    );
  }

  return (
    <div className="shell">
      <Link className="backlink" to="/">
        <ArrowLeftIcon /> Handbook
      </Link>

      <div className="doc-head">
        <div>
          <h1>{doc.title}</h1>
          {doc.summary && <p>{doc.summary}</p>}
          <div className="card-meta">
            <span className="tag">{KIND_LABEL[doc.kind]}</span>
            <span className="tag">{AUDIENCE_LABEL[doc.audience]}</span>
            {doc.team && <span className="tag tag-team">{doc.team}</span>}
            <span className={`badge badge-${doc.status}`}>{STATUS_LABEL[doc.status]}</span>
            <span>
              {doc.updated_by} · updated {formatDate(doc.updated_at)} · {doc.read_minutes} min
            </span>
          </div>
        </div>

        <div className="doc-actions">
          <button className="btn" onClick={() => navigate(`/d/${doc.slug}/edit`)}>
            Edit
          </button>
        </div>
      </div>

      <div className="doc-body">
        <Reader contentJson={doc.content_json} />
      </div>
    </div>
  );
}
